import { useRef } from 'react';
import { FaRegFilePdf } from 'react-icons/fa';
import classNames from 'classnames';
import { Tooltip } from '@mui/material';
import { Button } from './ui/button';
import { File, Trash } from 'lucide-react';

type FileUploadProps = {
  file: File | null;
  setFile: (file: File | null) => void;
  className?: string;
  maxFileSizeInMB?: number;
  hideRemove?: boolean;
};

const convertMBToBytes = (mb: number) => mb * 1024 * 1024;

// Utility function to validate file type and size
const validateFile = (file: File, maxFileSizeInMB: number): string | null => {
  if (file.type !== 'application/pdf') {
    return `${file.type} is not a supported file type. Please upload a PDF file.`;
  }

  if (file.size > convertMBToBytes(maxFileSizeInMB)) {
    return `File size exceeds ${maxFileSizeInMB}MB. Please upload a smaller file.`;
  }

  return null;
};

export function PdfFileUploader({
  file,
  setFile,
  className,
  maxFileSizeInMB = 2,
  hideRemove = false,
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];

      const validationError = validateFile(selectedFile, maxFileSizeInMB);
      if (validationError) {
        alert(validationError);
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];

      const validationError = validateFile(droppedFile, maxFileSizeInMB);
      if (validationError) {
        alert(validationError);
        return;
      }

      setFile(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <div className={classNames('col-span-full', className)}>
      {!file && (
        <div
          className=" flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10"
          onDragOver={handleDragOver}
          onDrop={handleFileDrop}
        >
          <div className="text-center">
            <FaRegFilePdf
              aria-hidden="true"
              className="text-primary-500 mx-auto h-12 w-12"
            />
            <div className="mt-4 flex text-sm leading-6 text-gray-700">
              <label
                htmlFor="file-upload"
                className="text-primary focus-within:ring-primary hover:text-primary-hover relative cursor-pointer rounded-md bg-white font-semibold focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2"
              >
                <span>Upload a file</span>
                <input
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs leading-5 text-gray-700">PDF up to {maxFileSizeInMB}MB</p>
          </div>
        </div>
      )}

      {file && (
        <div className="flex w-full flex-col items-center justify-between gap-4">
          <div className="flex w-full items-center gap-2 text-sm text-gray-700">
            <File className="text-primary h-5 w-5 flex-shrink-0" />
            <Tooltip title={file.name}>
              <span className="truncate">{file.name}</span>
            </Tooltip>
            {!hideRemove && (
              <Tooltip
                className="ml-auto"
                title={<span className="text-sm">Remove file</span>}
              >
                <Button
                  onClick={() => setFile(null)}
                  variant="secondary"
                  size="icon"
                >
                  <Trash className="h-4 w-4 flex-shrink-0 text-gray-700" />
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
